import * as React from 'react';
import { t } from '@lingui/core/macro';

export interface IConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  'data-testid'?: string;
  /** Unique ID for alert filtering (e.g. 'DIALOG_END_SESSION_NON_ORGANIZER'). */
  dialogId?: string;
  /**
   * Optional custom content to render below the message.
   * Use this to add form fields, additional information, or any custom UI.
   */
  children?: React.ReactNode;
}

/**
 * A reusable confirmation dialog component that replaces window.confirm.
 * Provides a consistent modal interface for user confirmation actions.
 */
export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = t`Confirm`,
  cancelText = t`Cancel`,
  onConfirm,
  onCancel,
  'data-testid': dataTestId,
  children = null,
}: IConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = React.useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  // Handle Escape key
  React.useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="relative z-[101]"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
      data-testid={dataTestId}
    >
      <div
        part="dialog-backdrop"
        className="fixed inset-0 transition-opacity"
        style={{
          backgroundColor: 'var(--ex3d-dialog-backdrop-color, var(--groupui-vwgroup-ref-color-deep-space-blue-900-80))',
        }}
        aria-hidden="true"
      ></div>
      <div className="fixed inset-0 z-[102] w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div
            part="dialog"
            className="relative overflow-hidden px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
            style={{
              backgroundColor: 'var(--ex3d-dialog-bg-color, var(--ex3d-dealer-white))',
              color: 'var(--ex3d-dialog-text-color, var(--ex3d-dealer-gray-900))',
              borderRadius: 'var(--ex3d-dialog-border-radius, 0.5rem)',
              fontFamily: 'var(--ex3d-dialog-font-family, inherit)',
            }}
          >
            <div>
              <div className="mt-3 sm:mt-2">
                <h2
                  part="dialog-title"
                  className="text-base font-semibold"
                  id="modal-title"
                  data-testid="confirm-dialog-heading"
                >
                  {title}
                </h2>
                {message && (
                  <div className="mt-2">
                    <p part="dialog-message" className="text-sm text-gray-700">
                      {message}
                    </p>
                  </div>
                )}
                {children && (
                  <div part="dialog-content" className="mt-4">
                    {children}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-5 sm:mt-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  part="dialog-cancel"
                  type="button"
                  onClick={handleCancel}
                  className="hover:bg-gray-50 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal sm:w-auto"
                  style={{
                    backgroundColor: 'var(--ex3d-dialog-bg-color, var(--ex3d-dealer-white))',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'var(--ex3d-dialog-cancel-border-color, var(--ex3d-dealer-gray-300))',
                    color: 'var(--ex3d-dialog-text-color, var(--ex3d-dealer-gray-700))',
                  }}
                  data-testid={dataTestId ? `${dataTestId}-cancel` : undefined}
                >
                  {cancelText}
                </button>
                <button
                  part="dialog-confirm"
                  type="button"
                  onClick={handleConfirm}
                  className="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal sm:w-auto"
                  style={{
                    backgroundColor: 'var(--ex3d-dialog-confirm-bg-color, var(--ex3d-dealer-primary))',
                    color: 'var(--ex3d-dialog-confirm-text-color, var(--ex3d-dealer-white))',
                  }}
                  data-testid={dataTestId ? `${dataTestId}-confirm` : undefined}
                >
                  {confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
